import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { tokenService } from '../lib/tokenService';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(tokenService.getToken());
  const [loading, setLoading] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Dual-role portal state
  const [showPortalSelector, setShowPortalSelector] = useState(false);
  const [activePortal, setActivePortal] = useState(tokenService.getActivePortal());
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // Check for available roles (dual-role support)
      const roles = response.data.available_roles || [response.data.role];
      setAvailableRoles(roles);
      
      // If user has multiple roles, check if portal is already selected
      if (roles.length > 1) {
        const savedPortal = localStorage.getItem('active_portal');
        if (!savedPortal) {
          // Show portal selector
          setShowPortalSelector(true);
        } else {
          setActivePortal(savedPortal);
        }
      } else {
        // Single role user - set activePortal to their role
        const userRole = response.data.role;
        setActivePortal(userRole);
        localStorage.setItem('active_portal', userRole);
      }
      
      // If admin (or admin role selected), check license status and onboarding
      if (response.data.role === 'admin' || activePortal === 'admin') {
        await checkLicenseAndOnboarding();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const checkLicenseAndOnboarding = async () => {
    try {
      // Get license status
      const licenseRes = await axios.get(`${API_URL}/saas/licenses/status`);
      setLicenseStatus(licenseRes.data);
      
      // Get company info
      try {
        const companyRes = await axios.get(`${API_URL}/saas/my-company`);
        setCompanyInfo(companyRes.data);
        
        // Check if this is first login (no onboarding_completed flag)
        if (!companyRes.data.onboarding_completed && !localStorage.getItem('onboarding_skipped')) {
          setShowOnboarding(true);
        }
      } catch (e) {
        // No company associated
      }
    } catch (error) {
      console.error('Error checking license:', error);
    }
  };

  const completeOnboarding = async () => {
    setShowOnboarding(false);
    try {
      await axios.put(`${API_URL}/saas/my-company`, { onboarding_completed: true });
    } catch (e) {
      // Silently fail
    }
  };

  const skipOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('onboarding_skipped', 'true');
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    tokenService.setToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // Check for available roles
    const roles = userData.available_roles || [userData.role];
    setAvailableRoles(roles);
    
    // If multiple roles, show portal selector
    if (roles.length > 1) {
      tokenService.setActivePortal(null); // Reset on new login
      setShowPortalSelector(true);
    } else {
      setActivePortal(userData.role);
      localStorage.setItem('active_portal', userData.role);
    }
    
    // Check license for admin
    if (userData.role === 'admin') {
      await checkLicenseAndOnboarding();
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    tokenService.setToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = async () => {
    // Clear local state immediately
    tokenService.clearAllSync();
    localStorage.removeItem('onboarding_skipped');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setLicenseStatus(null);
    setCompanyInfo(null);
    setShowPortalSelector(false);
    setActivePortal(null);
    setAvailableRoles([]);
    
    // Call server to clear httpOnly cookie (fire and forget)
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore - local state is already cleared
    }
  };

  // Portal selection for dual-role users
  const selectPortal = (portal) => {
    setActivePortal(portal);
    tokenService.setActivePortal(portal);
    setShowPortalSelector(false);
    
    // If switching to admin portal, check onboarding
    if (portal === 'admin') {
      checkLicenseAndOnboarding();
    }
  };

  // Allow switching portals (for admins with agent access)
  const switchPortal = () => {
    if (availableRoles.length > 1) {
      tokenService.setActivePortal(null);
      setActivePortal(null);
      setShowPortalSelector(true);
    }
  };

  const isAdmin = () => user?.role === 'admin' || (activePortal === 'admin' && availableRoles.includes('admin'));
  const isAgent = () => user?.role === 'agent' || activePortal === 'agent';
  const isSuperAdmin = () => user?.role === 'super_admin';
  const canSwitchPortal = () => availableRoles.length > 1;
  
  // Check if trial is expired and no paid licenses
  const isTrialExpired = () => {
    if (!licenseStatus) return false;
    return !licenseStatus.is_trial && 
           licenseStatus.total_licenses === 0 &&
           user?.role === 'admin';
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isAgent,
    isSuperAdmin,
    licenseStatus,
    companyInfo,
    showOnboarding,
    completeOnboarding,
    skipOnboarding,
    isTrialExpired,
    refreshLicenseStatus: checkLicenseAndOnboarding,
    // Dual-role portal management
    showPortalSelector,
    activePortal,
    availableRoles,
    selectPortal,
    switchPortal,
    canSwitchPortal
  }), [
    user, token, loading, licenseStatus, companyInfo, showOnboarding,
    showPortalSelector, activePortal, availableRoles
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
